import { bearerAuth } from "../standardAuthPatterns";
import { defineProduct, productVariant } from "../types";
import { oauthSetupSteps } from "../oauthSetupSteps";
import { oauthAuth } from "../standardAuthPatterns";
import logo from "./logo.svg";

const credential = productVariant({
  authLabel: "Existing user access token",
  identifier: "x",
  protocol: "http",
  defaultUrlPattern: () => "https://api\\.x\\.com/.*",
  auth: (app) => bearerAuth(app.secret.bearerToken),
  steps: [
    {
      title: "Create an OAuth 2.0 user access token",
      items: [
        {
          kind: "markdown",
          content:
            "Follow the official docs at [https://docs.x.com/fundamentals/authentication/oauth-2-0/overview](https://docs.x.com/fundamentals/authentication/oauth-2-0/overview) and [https://docs.x.com/x-api/users/get-my-user](https://docs.x.com/x-api/users/get-my-user) to create an OAuth 2.0 user access token for the X API. The `/2/users/me` endpoint requires user context and the `users.read` scope.",
        },
      ],
    },
    {
      title: "Enter your connection details",
      items: [
        {
          kind: "input",
          label: "OAuth 2.0 user access token",
          required: true,
          target: {
            kind: "secrets",
            field: "bearerToken",
          },
        },
        {
          kind: "test-url",
          label: "Test URL",
          placeholder: "https://api.x.com/",
          helperText:
            "Optional. Set this if you want testing to hit a specific endpoint instead of the default draft URL.",
          urlTarget: {
            kind: "configuration",
            field: "testUrl",
          },
          methodTarget: {
            kind: "configuration",
            field: "testMethod",
          },
        },
      ],
    },
  ],
  connectionTest: {
    protocol: "http",
    request: ({ testUrl, testMethod }) => ({
      url: testUrl ?? (() => "https://api.x.com/2/users/me")(),
      method: testMethod === "POST" ? "POST" : "GET",
    }),
  },
});

export const x = defineProduct({
  name: "X",
  tagLine: "X platform APIs",
  logo,
  tags: ["communication"],
  variants: [
    credential,
    productVariant({
      identifier: "xOAuth",
      authLabel: "OAuth",
      protocol: "http",
      defaultUrlPattern: () => "https://api\\.x\\.com/2/.*",
      auth: oauthAuth,
      steps: oauthSetupSteps({
        identifier: "xOAuth",
        registrationUrl: "https://console.x.com/",
        scopes: ["tweet.read", "users.read", "offline.access"],
        instructions:
          "Enable OAuth 2.0 and configure a Web App or Automated App confidential client.",
      }),
      oauth: {
        grantType: () => "authorization_code",
        authorizationEndpoint: () => "https://x.com/i/oauth2/authorize",
        tokenEndpoint: () => "https://api.x.com/2/oauth2/token",
        tokenEndpointAuthMethod: () => "client_secret_basic",
        clientCredentialsFrom: "db",
        scopes: () => ["tweet.read", "users.read", "offline.access"],
        pkce: () => true,
      },
      connectionTest: {
        protocol: "http",
        request: () => "https://api.x.com/2/users/me",
      },
    }),
  ],
});
